'use client'

import { Radio } from '@base-ui/react/radio'
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group'
import { cva } from 'class-variance-authority'
import { useFieldControl } from './field'
import type { TestIdProps } from '../internal/test-id'

/**
 * One choice from a few, with every option visible.
 *
 * The gap this fills: the package had Checkbox and Switch and no radio at all,
 * so the one control that says "exactly one of these" had to be hand-rolled.
 *
 * A radio group is not a Select. Use this when the options are few and seeing
 * them all is part of the decision, and a Select when the list is long enough
 * that showing it costs more than it explains. Base UI owns the part that is
 * expensive to get right by hand: a radio group is a single tab stop, and the
 * arrow keys move between options and change the value as they go.
 *
 * Options are data rather than children, as in Select and Combobox, because a
 * component taking arbitrary children takes arbitrary markup and this one has
 * an ARIA contract to keep.
 *
 * Wrap it in a Field with `isControlLabelled`, which names the group rather
 * than any one option.
 */
export type RadioOption = {
    value: string
    /** What the user reads beside the dot. */
    label: string
    /** A second line under the label, for the consequence of choosing it. */
    description?: string
    isDisabled?: boolean
}

const listVariants = cva('flex', {
    variants: {
        orientation: {
            vertical: 'flex-col gap-3',
            horizontal: 'flex-row flex-wrap gap-x-6 gap-y-3'
        }
    },
    defaultVariants: { orientation: 'vertical' }
})

export type RadioGroupProps = TestIdProps & {
    options: RadioOption[]
    value?: string
    defaultValue?: string
    onValueChange?: (value: string) => void
    isDisabled?: boolean
    /** Horizontal suits two short options. Anything longer wraps badly. */
    orientation?: 'vertical' | 'horizontal'
    name?: string
    /** Names the group when it is used outside a Field. Invisible, like Select's. */
    ariaLabel?: string
}

export const RadioGroup = ({
    options,
    value,
    defaultValue,
    onValueChange,
    isDisabled = false,
    orientation = 'vertical',
    name,
    ariaLabel,
    'data-testid': testId
}: RadioGroupProps) => {
    const field = useFieldControl()

    return (
        <RadioGroupPrimitive
            data-testid={testId}
            value={value}
            defaultValue={defaultValue}
            // Base UI passes (value, eventDetails). Normalised to the string our
            // public type promises, so the primitive's event object stops here.
            onValueChange={(next) => onValueChange?.(next)}
            disabled={isDisabled}
            name={name}
            aria-label={ariaLabel}
            aria-describedby={field?.describedBy}
            aria-invalid={field?.isInvalid}
            required={field?.isRequired}
            className={listVariants({ orientation })}
        >
            {options.map((option) => (
                // The label wraps the control, so the text is part of the hit
                // area without needing an id per option. A 13px dot on its own
                // is a target nobody can hit reliably.
                <label
                    key={option.value}
                    className="group flex cursor-pointer items-start gap-2 has-disabled:cursor-not-allowed"
                >
                    <Radio.Root
                        value={option.value}
                        disabled={option.isDisabled}
                        className="border-border bg-background data-checked:border-border-brand focus-visible:ring-ring aria-[invalid=true]:border-border-danger-strong mt-0.5 flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none data-disabled:cursor-not-allowed data-disabled:opacity-50"
                    >
                        <Radio.Indicator className="bg-background-brand size-2 rounded-full" />
                    </Radio.Root>

                    <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-foreground text-aria-base leading-none select-none">{option.label}</span>
                        {option.description ? (
                            <span className="text-foreground-muted text-aria-xs leading-normal select-none">
                                {option.description}
                            </span>
                        ) : null}
                    </span>
                </label>
            ))}
        </RadioGroupPrimitive>
    )
}
